/* eslint-disable max-lines */
import React, { Fragment, Suspense, lazy } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import { ErrorBoundary, SectionErrorFallback } from '@modernloop/shared/helper-components';

import DashboardLayout from 'src/layouts/DashboardLayout';

import LoadingScreen from 'src/shared/components/base/screenLoaders/LoadingScreen';
import UserHasNoOrgGuard from 'src/shared/guards/UserHasNoOrgGuard';
import UserIsAuthenticatedGuard from 'src/shared/guards/UserIsAuthenticatedGuard';
import UserIsLoggedOutGuard from 'src/shared/guards/UserIsLoggedOutGuard';

import RedirectWithQueryParams from './components/Link/RedirectWithQueryParams';
import { MetricsPageTabs, Routes as RoutesList } from './constants/routes';
import IPOrgIntegrationSetupGuard from './interviewer-portal/guards/IPOrgIntegrationSetupGuard';
import IPUserHasAcceptedTOSGuard from './interviewer-portal/guards/IPUserHasAcceptedTOSGuard';
import IPUserHasInterviewPermissionGuard from './interviewer-portal/guards/IPUserHasInterviewPermissionGuard';
import IPUserNotInIframeGuard from './interviewer-portal/guards/IPUserNotInIframeGuard';
import OrgSettingsLayout from './layouts/OrgSettingsLayout';
import RPOrgHasConnectedDirectoryGuard from './recruiting-portal/guards/RPOrgHasConnectedDirectoryGuard';
import RPOrgIntegrationSetupGuard from './recruiting-portal/guards/RPOrgIntegrationSetupGuard';
import RPUserHasFullyOnboardGuard from './recruiting-portal/guards/RPUserHasFullyOnboardedGuard';
import RPUserHasSchedulerPermissionGuard from './recruiting-portal/guards/RPUserHasSchedulerPermissionGuard';
import UserHasOrgGuard from './shared/guards/UserHasOrgGuard';
import { AGENT_ROUTE_PATHS } from './views-new/AIAssistant/agent/agentRoutes';

type GuardComponent = React.ComponentType<{ children: React.ReactNode }>;
type LayoutComponent = React.ComponentType<{ children: React.ReactNode }>;

type Routes = {
  path?: string | string[];

  // Gaurds in order of outermost to innermost
  guards?: GuardComponent[];
  layout?: LayoutComponent;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component?: React.ComponentType<any>;
  routes?: Routes;
}[];

export const renderRoutes = (routes: Routes = []): JSX.Element => (
  <Suspense fallback={<LoadingScreen sx={{ bgcolor: (theme) => theme.palette.background.default }} />}>
    <ErrorBoundary>
      <Switch>
        {routes.map((route, i) => {
          const guards = route.guards || [Fragment];
          const Layout = route.layout || Fragment;
          const Component = route.component;

          // If the route has a path and no nested routes, it's an exact route
          const exact: boolean = !!route.path && !route.routes;

          return (
            <Route
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              path={route.path}
              exact={exact}
              render={(props) => {
                let guardedComponent = (
                  <Layout>
                    {/* eslint-disable-next-line no-nested-ternary */}
                    {route.routes ? (
                      renderRoutes(route.routes)
                    ) : Component ? (
                      // Route-scoped boundary: a crashing view is replaced by a
                      // compact fallback while the layout (navigation, chrome)
                      // stays alive; the scope attributes the error per route in
                      // observability. The outer boundary remains as a backstop.
                      <ErrorBoundary
                        fallback={<SectionErrorFallback minHeight="60vh" />}
                        scope={`route:${String(route.path ?? 'unknown')}`}
                      >
                        <Component {...props} />
                      </ErrorBoundary>
                    ) : null}
                  </Layout>
                );
                // Loop through in reverse order to build the innermost component first
                for (let j = guards.length - 1; j >= 0; j--) {
                  const Guard = guards[j];
                  guardedComponent = <Guard>{guardedComponent}</Guard>;
                }

                return guardedComponent;
              }}
            />
          );
        })}
      </Switch>
    </ErrorBoundary>
  </Suspense>
);

// TODO: (Cam) - ENG-14998 - Move routes to specific app directory
export const recruitingDashboardRoutes: Routes = [
  {
    path: '/404',
    component: lazy(() => import('src/views/errors/NotFoundView')),
  },
  {
    path: '/logout',
    component: lazy(() => import('src/shared/components/base/screenLoaders/SplashScreen')),
  },
  {
    guards: [UserIsLoggedOutGuard],
    path: '/login',
    component: lazy(() => import('src/views-new/auth/LoginView')),
  },
  {
    path: '/close',
    component: lazy(() => import('src/views/CloseBrowserView')),
  },
  {
    guards: [UserIsLoggedOutGuard],
    path: '/register',
    component: lazy(() => import('src/views-new/auth/LoginView')),
  },
  {
    path: '/callback',
    component: lazy(() => import('src/shared/components/base/screenLoaders/SplashScreen')),
  },
  {
    path: '/oauth/consent/:authorizationRequestId',
    guards: [UserIsAuthenticatedGuard],
    component: lazy(() => import('src/views-new/OAuthConsent')),
  },
  {
    path: '/no-org',
    guards: [UserIsAuthenticatedGuard, UserHasNoOrgGuard],
    component: lazy(() => import('src/views/errors/NoOrgView')),
  },
  /* *
   * This is the org setup route for an organization.
   * To access this route we need to check if the user is authenticated, has an org & has scheduler/admin permission.
   * */
  {
    path: '/onboarding',
    guards: [UserIsAuthenticatedGuard, UserHasOrgGuard, RPUserHasSchedulerPermissionGuard],
    component: lazy(() => import('src/views-new/OrgSetup')),
  },
  {
    path: RoutesList.AiScheduling,
    guards: [UserIsAuthenticatedGuard, UserHasOrgGuard],
    component: lazy(() => import('src/views-new/AiTimeline/AiTimelinePage')),
  },
  {
    path: '/',
    /* *
     * The sequence of guards is important.
     *
     * UserIsAuthenticatedGuard           - checks if the user is authenticated. Depends on the AuthContext.
     *                                      No BE APIS are accessed here so this is shared for RC & Interviewer portal
     * UserHasOrgGuard                    - Checks if the user has an org, This also depends on the AuthContext
     *                                      No BE APIS are accessed here so this is shared for RC & Interviewer portal
     * RPUserHasSchedulerPermissionGuard  - Checks if the user has scheduler permission, this is specific to RC/Admin.
     *                                      Including this guard any gaurd accessing BE APIs queries should be placed after this.
     *                                      Reason the queries after this gaurd are accessed from /app-client/src/generated/mloop-graphql.tsx
     * RPOrgSetupGuard                    - Checks if the org is setup with mandatory required setup.
     * RPUserHasFullyOnboardGuard         - Checks if the user is fully onboarded with mandatory required setup.
     *                                      Currently for Directory & Terms & Condition.
     * */
    guards: [
      UserIsAuthenticatedGuard,
      UserHasOrgGuard,
      RPUserHasSchedulerPermissionGuard,
      RPOrgIntegrationSetupGuard,
      RPUserHasFullyOnboardGuard,
    ],
    routes: [
      {
        // Public-ID deep links (app.modernloop.io/id/<publicId>) resolve to the best in-app URL
        // via getUrlForPublicId so external clients never need to know app routes.
        path: '/id/:publicId',
        component: lazy(() => import('src/views-new/PublicIdRedirect')),
      },
      {
        path: '/extension',
        component: lazy(() => import('src/recruiting-portal/pages/extension/sidebar')),
      },
      {
        path: '/extension-redirect',
        component: lazy(() => import('src/recruiting-portal/pages/extension/Redirect')),
      },
      {
        path: '/terms-and-privacy',
        component: lazy(() => import('src/recruiting-portal/pages/TermsAndPrivacy')),
      },
      {
        path: '/grant-access/:integrationType',
        component: lazy(() => import('src/views-new/Onboarding/GrantAccess')),
      },
      {
        path: '/gql',
        component: lazy(() => import('src/recruiting-portal/pages/DevGqlExplorer')),
      },
      {
        // Redirect to the new settings page. This route is deprecated and shouldn't be used.
        // We need to keep the redirect support for this route
        path: '/organization/integrations',
        component: () => <RedirectWithQueryParams to="/settings/organization/integrations" />,
      },
      {
        path: '/settings/organization',
        layout: OrgSettingsLayout,
        routes: [
          {
            path: '/settings/organization',
            component: lazy(() => import('src/views-new/Settings')),
          },
          {
            path: '/settings/organization/:tab',
            component: lazy(() => import('src/views-new/Settings')),
          },
          {
            component: () => <Redirect to="/404" />,
          },
        ],
      },
      {
        path: '/settings/account',
        layout: OrgSettingsLayout,
        routes: [
          {
            path: '/settings/account',
            component: lazy(() => import('src/views-new/account/AccountView')),
          },
          {
            path: '/settings/account/:tab',
            component: lazy(() => import('src/views-new/account/AccountView')),
          },
          {
            component: () => <Redirect to="/404" />,
          },
        ],
      },
      {
        path: '/',
        guards: [RPOrgHasConnectedDirectoryGuard],
        layout: DashboardLayout,
        routes: [
          {
            path: [...AGENT_ROUTE_PATHS],
            component: lazy(() => import('src/views-new/AIAssistant/AgentFullPage')),
          },
          {
            path: '/candidates',
            component: lazy(() => import('src/views-new/CandidateList')),
          },
          {
            path: ['/candidates/:candidateID', '/candidates/:candidateID/:tab'],
            component: lazy(() => import('src/views-new/CandidateDetails')),
          },
          {
            path: ['/upcomingevents', '/upcomingevents/:tab'],
            component: lazy(() => import('src/views-new/UpcomingEventsView')),
          },
          {
            path: '/employees',
            component: lazy(() => import('src/views-new/Employee/EmployeeListView')),
          },
          {
            path: '/interviewers',
            component: lazy(() => import('src/views-new/Employee/EmployeeListView')),
          },
          {
            path: '/interviewers/:employeeID',
            component: lazy(() => import('src/views-new/InterviewerDetailsPage')),
          },
          {
            path: '/interviewers/:employeeID/:tab',
            component: lazy(() => import('src/views-new/InterviewerDetailsPage')),
          },
          {
            path: '/schedule/:applicationID',
            component: lazy(() => import('src/views-new/ScheduleFlow')),
          },
          {
            path: '/schedule/:applicationID/new',
            component: lazy(() => import('src/views-new/ScheduleFlow')),
          },
          {
            path: '/schedule/:applicationID/done',
            component: lazy(() => import('src/views-new/ScheduleFlow/ScheduleFlowDone')),
          },
          {
            path: '/debrief/:applicationID/done',
            component: lazy(() => import('src/views-new/Debrief/DebriefFlowDone')),
          },
          {
            path: '/update/:applicationStageId/done',
            component: lazy(() => import('src/views-new/UpdateFlow/UpdateFlowDone')),
          },
          {
            path: '/reschedule/:applicationStageID',
            component: lazy(() => import('src/views-new/RescheduleFlow')),
          },
          {
            path: '/update/:applicationStageId',
            component: lazy(() => import('src/views-new/UpdateFlow')),
          },
          {
            path: '/debrief/:applicationId/',
            component: lazy(() => import('src/views-new/Debrief')),
          },
          {
            path: '/metrics/:tab',
            component: lazy(() => import('src/views-new/MetricsPage')),
          },
          {
            path: RoutesList.Notifications,
            component: lazy(() => import('src/views-new/NotificationsPage')),
          },
          {
            path: '/jobs',
            component: lazy(() => import('src/views-new/Jobs')),
          },
          {
            path: '/jobs/:jobID',
            component: lazy(() => import('src/views-new/JobDetailsPage')),
          },
          {
            path: '/job-settings-import',
            component: lazy(() => import('src/views-new/JobImportSettings')),
          },
          {
            path: '/jobs/:jobID/:tab',
            component: lazy(() => import('src/views-new/JobDetailsPage')),
          },
          {
            path: '/jobs/:jobID/:tab/:jobStageID',
            component: lazy(() => import('src/views-new/JobDetailsPage')),
          },
          {
            path: '/interviews',
            component: lazy(() => import('src/views-new/InterviewModule')),
          },
          {
            path: '/interviews/:interviewID',
            component: lazy(() => import('src/views-new/InterviewModuleDetails')),
          },
          {
            path: '/interviews/:interviewID/:tab',
            component: lazy(() => import('src/views-new/InterviewModuleDetails')),
          },
          {
            path: '/metrics',
            component: () => <Redirect to={`/metrics/${MetricsPageTabs.RECRUITERS}`} />,
          },
          {
            path: [RoutesList.MySchedulingTasks, `${RoutesList.MySchedulingTasks}/:tab`],
            component: lazy(() => import('src/views-new/SchedulingTasks/MySchedulingTasks')),
          },
          {
            path: RoutesList.AllSchedulingTasks,
            component: lazy(() => import('src/views-new/SchedulingTasks/AllSchedulingTasks')),
          },
          {
            path: RoutesList.TaskSuggestedSchedules,
            component: lazy(() => import('src/recruiting-portal/pages/TaskSuggestedSchedules')),
          },
          {
            path: RoutesList.TranscriptInterview,
            component: lazy(() => import('src/recruiting-portal/pages/InterviewTranscript')),
          },
          {
            path: ['/', '/app'],
            component: lazy(() => import('src/views-new/SchedulingTasks/MySchedulingTasks')),
          },
          {
            component: () => <Redirect to="/404" />,
          },
        ],
      },
      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },
];

// TODO: (Cam) - ENG-14998 - Move routes to specific app directory
export const interviewerRoutes: Routes = [
  /* *
   * This is the blocking org setup route for an organization.
   * To access this route we need to check if the user is authenticated, has an org & has Interviewer permission.
   * */
  {
    path: '/ip/onboarding',
    guards: [UserIsAuthenticatedGuard, UserHasOrgGuard, IPUserHasInterviewPermissionGuard],
    component: lazy(() => import('src/interviewer-portal/pages/OrgSetup')),
  },
  {
    path: '/ip',
    /* *
     * The sequence of guards is important.
     *
     * UserIsAuthenticatedGuard           - checks if the user is authenticated. Depends on the AuthContext.
     *                                      No BE APIS are accessed here so this is shared for RC & Interviewer portal
     * UserHasOrgGuard                    - Checks if the user has an org, This also depends on the AuthContext
     *                                      No BE APIS are accessed here so this is shared for RC & Interviewer portal
     * IPUserHasInterviewPermissionGuard  - Checks if the user has scheduler permission, this is specific to Interviewer.
     *                                      Including this guard any gaurd accessing BE APIs queries should be placed after this.
     *                                      Reason the queries after this Gaurd are accessed from src/interviewer-portal/generated/graphql'
     * IPOrgSetupGuard                    - Checks if the org is setup with mandatory required setup.
     * */
    guards: [UserIsAuthenticatedGuard, UserHasOrgGuard, IPUserHasInterviewPermissionGuard, IPOrgIntegrationSetupGuard],
    routes: [
      {
        path: '/ip/gql',
        component: lazy(() => import('src/interviewer-portal/pages/DevGqlExplorer')),
      },
      {
        path: '/ip/terms-and-privacy',
        component: lazy(() => import('src/interviewer-portal/pages/TermsAndPrivacy')),
      },
      {
        path: '/ip/extension',
        component: lazy(() => import('src/interviewer-portal/pages/extension/ExtensionSidebarPage')),
      },
      {
        path: '/ip',
        guards: [IPUserNotInIframeGuard, IPUserHasAcceptedTOSGuard],
        routes: [
          {
            path: '/ip',
            component: lazy(() => import('src/interviewer-portal/pages/InterviewerInfo')),
          },
          {
            path: '/ip/:interviewerId',
            component: lazy(() => import('src/interviewer-portal/pages/InterviewerInfo')),
          },
          {
            component: () => <Redirect to="/404" />,
          },
        ],
      },
      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },
  {
    component: () => <Redirect to="/404" />,
  },
];

export default recruitingDashboardRoutes;
